/**
 * DisposalExclusionsSection — "Never send this customer's water to…" on the customer form
 * (Phase 28 / task 28.5, DEC-331 D-5, D-14 A).
 *
 * Each entry names a place and why. It never blocks: drivers and dispatchers still see the
 * place, with a red "Customer excluded" badge and this reason, and must type a reason to
 * pick it. Suggestions never recommend it. Changes save at once (they are separate records
 * from the customer's details), are audited, and are ended rather than deleted.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { Combobox } from '../../components/Combobox';
import { fieldStyle, labelStyle } from './form-bits';
import { subText } from './ui-bits';
import {
  buildExclusionBody,
  exclusionPlaceOptions,
  useAddDisposalExclusion,
  useDisposalExclusions,
  useEndDisposalExclusion,
  useExclusionPlaces,
  type DisposalExclusion,
} from './disposal-exclusions';

function EndExclusionButton({ x }: { x: DisposalExclusion }): React.JSX.Element {
  const end = useEndDisposalExclusion(x.haulerOperatorId, x.id);
  return (
    <button
      type="button"
      disabled={end.isPending}
      onClick={(): void =>
        end.mutate(
          {},
          {
            onSuccess: () => toast.success('Removed from the list'),
            onError: () => toast.error('Could not remove it'),
          },
        )
      }
      style={{
        fontSize: '12px',
        color: 'var(--color-danger)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
      }}
    >
      Remove
    </button>
  );
}

export function DisposalExclusionsSection({
  haulerOperatorId,
}: {
  readonly haulerOperatorId: string;
}): React.JSX.Element {
  const { data, isLoading, isError } = useDisposalExclusions(haulerOperatorId);
  const [adding, setAdding] = React.useState(false);
  const [place, setPlace] = React.useState('');
  const [reason, setReason] = React.useState('');
  const { data: places } = useExclusionPlaces(adding);
  const add = useAddDisposalExclusion(haulerOperatorId);

  const active = React.useMemo(() => data?.exclusions ?? [], [data]);
  const options = React.useMemo(() => exclusionPlaceOptions(places, active), [places, active]);
  const built = buildExclusionBody(haulerOperatorId, place, reason);

  const submit = (): void => {
    if (!built.ok) return;
    add.mutate(built.body, {
      onSuccess: () => {
        toast.success('Added to the list');
        setPlace('');
        setReason('');
        setAdding(false);
      },
      onError: () => toast.error('Could not add it. It may already be on the list.'),
    });
  };

  return (
    <section
      aria-labelledby="excl-title"
      style={{ borderTop: '1px solid var(--color-border-secondary)', paddingTop: '12px' }}
    >
      <h3 id="excl-title" style={{ ...labelStyle, marginBottom: '4px' }}>
        Never send this customer&apos;s water to
      </h3>
      <p style={{ ...subText, marginBottom: '8px' }}>
        Drivers and dispatchers still see these places, marked &ldquo;Customer excluded&rdquo;, and
        must give a reason to choose one. Suggestions never pick them.
      </p>
      {isLoading && <p style={subText}>Loading…</p>}
      {isError && (
        <p style={{ ...subText, color: 'var(--color-danger)' }}>Could not load the list.</p>
      )}
      {!isLoading && !isError && active.length === 0 && <p style={subText}>None.</p>}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {active.map((x) => (
          <li
            key={x.id}
            style={{ display: 'flex', alignItems: 'baseline', gap: '8px', padding: '4px 0' }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{x.targetName ?? 'Unknown place'}</div>
              <div style={subText}>{x.reason}</div>
            </div>
            <EndExclusionButton x={x} />
          </li>
        ))}
      </ul>
      {adding ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
          <label htmlFor="excl-place" style={labelStyle}>
            Place
          </label>
          <Combobox
            id="excl-place"
            value={place}
            onChange={setPlace}
            options={options}
            placeholder="Pick a place"
            searchPlaceholder="Search disposal places…"
            emptyText="No places match."
          />
          <label htmlFor="excl-reason" style={labelStyle}>
            Why
          </label>
          <input
            className="keep-case"
            id="excl-reason"
            value={reason}
            maxLength={500}
            onChange={(e): void => setReason(e.target.value)}
            placeholder="e.g. customer contract, past contamination dispute"
            style={fieldStyle}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button type="button" disabled={!built.ok || add.isPending} onClick={submit}>
              {add.isPending ? 'Adding…' : 'Add to list'}
            </Button>
            <Button type="button" variant="outline" onClick={(): void => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={(): void => setAdding(true)}
          style={{ marginTop: '8px' }}
        >
          Add a place
        </Button>
      )}
    </section>
  );
}
